import {
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dateToString,
  getFirstDayOfWeek,
  getWeekdayInitials,
  localeNormalizer,
} from '@youshift/shared/utils';
import { Section } from '@youshift/shared/types';
import { useAssignSlotMutation } from '@youshift/shared/hooks/mutations';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import FilterDropdown from '../../../components/FilterDropdown';
import UsersFilterDropdown from '../../../components/FilterDropdownWithSubgroups';
import { VerticalVirgueria } from '../../../components/ScheduleGrids/VerticalSlotGridAssignment';
import { ScheduleGridAssignment } from '../../../components/ScheduleGrids/ScheduleGridAssignment';
import Wrapper from '../../../components/Wrapper';
import { useChainContext } from '../../../layouts/ChainLayout';
import { useItrContext } from '../../../layouts/IterationRootLayout/IterationRootLayout';
import { VirgueriaLayoutMode } from '../../../components/ScheduleGrids/types';
import ShiftLabelLegend from '../../../components/Calendars/ShiftLabelLegend';
import Virgueria from '../../../components/ManualAssignment/Virgueria';
import { VirgueriaVersion } from '../../../components/ManualAssignment/helpers';
import Modal from '../../../components/Modal';
import i18n from '../../../utils/i18n';
import { YSButton } from '../../../components/Buttons';
import { LabelBadge } from '../../../components/Calendars/LabelBadge';
import { findAllOverlappingSlotsRestPeriod } from '../../../utils/iteration_checks/utils';

export default function ManualAssignment() {
  const { t } = useTranslation();
  const { idItr } = useParams();
  const {
    itrUsers,
    sectionsWithSlots,
    iteration,
    users,
    roles,
    status,
    epa,
    sectionSlotsDict,
  } = useItrContext();
  const { slotLabels } = useChainContext();
  const queryClient = useQueryClient();

  const [selectedPeople, setSelectedPeople] = useState(
    new Set(Object.keys(itrUsers).map(Number)),
  );
  const [selectedSectionsFilter, setSelectedSectionsFilter] = useState(
    new Set(sectionsWithSlots.map(({ section }) => String(section.id_section))),
  );
  const [showRestPeriod, setShowRestPeriod] = useState(false);

  const [virgueriaLayout, setVirgueriaLayout] = useState<VirgueriaLayoutMode>(
    VirgueriaLayoutMode.HORIZONTAL,
  );

  // BULK ASSIGNMENT STUFF
  const locale = localeNormalizer(i18n.language);
  const daysOfWeek = getWeekdayInitials(t, locale);
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<
    Set<number>
  >(new Set());
  const [bulkAssignmentModalOpen, setBulkAssignmentModalOpen] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<Set<number>>(
    new Set(
      Object.values(slotLabels).length
        ? Object.values(slotLabels).map(label => label.id_slot_label)
        : [-1],
    ),
  );
  const [sectionSlotsSelectedTimeFrames, setSectionSlotsSelectedTimeFrames] =
    useState<Set<string>>(new Set());
  const [sectionSlotsSelectedDaysOfWeek, setSectionSlotsSelectedDaysOfWeek] =
    useState<Set<number>>(new Set(Object.keys(daysOfWeek).map(Number)));
  // Add state for selected sections
  const sections = useMemo(
    () =>
      Object.values(sectionsWithSlots).reduce<Record<number, Section>>(
        (acc, { section }) => {
          acc[section.id_section] = section;
          return acc;
        },
        {},
      ),
    [sectionsWithSlots],
  );
  const [selectedSectionsBulkAssignment, setSelectedSectionsBulkAssignment] =
    useState<Set<number>>(new Set(Object.keys(sections).map(Number)));

  const resetDefaults = () => {
    setSelectedSectionsBulkAssignment(
      new Set(Object.keys(sections).map(Number)),
    );
    setSectionSlotsSelectedDaysOfWeek(
      new Set(Object.keys(daysOfWeek).map(Number)),
    );
    setSectionSlotsSelectedTimeFrames(new Set());
    setSelectedLabels(
      new Set(Object.values(slotLabels).map(label => label.id_slot_label)),
    );
  };

  const assignSlotMutation = useAssignSlotMutation(queryClient, {
    onSuccess: (data, variables) => {
      toast.success(t('manager.shiftAssignment.assignSuccess'));
      queryClient.invalidateQueries({ queryKey: ['shiftAssignments', idItr] });
      setBulkAssignmentModalOpen(false);
      setSelectedUsersToAssign(new Set());
      resetDefaults();
    },
  });

  const sectionSlotsUniqueTimeFrames = useMemo(() => {
    const timeFrames: Set<string> = new Set();
    Object.values(sectionSlotsDict || {}).forEach(sectionSlot => {
      // Check if the section is selected
      const isSectionSelected = selectedSectionsBulkAssignment.has(
        sectionSlot.id_section,
      );

      // Check if the day of week is selected
      const slotStart = new Date(sectionSlot.start);
      const slotDayOfWeek = slotStart.getUTCDay();
      const isDayOfWeekSelected =
        sectionSlotsSelectedDaysOfWeek.has(slotDayOfWeek);

      // Check if there are any labels at all
      const hasLabels = sectionSlot.slot_labels.length > 0;

      // If there are labels, check if any are selected
      // If there are no labels at all, consider it as valid (don't filter by labels)
      const isLabelValid =
        !hasLabels ||
        sectionSlot.slot_labels.some(labelId => selectedLabels.has(labelId));

      // Only add timeframe if section and day of week are selected, and labels are valid
      if (isSectionSelected && isDayOfWeekSelected && isLabelValid) {
        timeFrames.add(
          `${dateToString(sectionSlot.start, 'hh:mm', locale)} - ${dateToString(sectionSlot.end, 'hh:mm', locale)}`,
        );
      }
    });
    return Array.from(timeFrames).sort((a, b) => {
      const [aStart, aEnd] = a.split(' - ');
      const [bStart, bEnd] = b.split(' - ');

      // Convert times to Date objects for proper comparison
      const aStartDate = new Date(`2002-10-13 ${aStart}`);
      const bStartDate = new Date(`2002-10-13 ${bStart}`);
      const aEndDate = new Date(`2002-10-13 ${aEnd}`);
      const bEndDate = new Date(`2002-10-13 ${bEnd}`);

      // First sort by start time
      const startComparison = aStartDate.getTime() - bStartDate.getTime();
      // If start times are the same, sort by end time
      return startComparison !== 0
        ? startComparison
        : aEndDate.getTime() - bEndDate.getTime();
    });
  }, [
    sectionSlotsDict,
    locale,
    selectedSectionsBulkAssignment,
    sectionSlotsSelectedDaysOfWeek,
    selectedLabels,
  ]);

  const bulkAssignSlots = () => {
    const sectionSlotsToAssign = new Set(
      Object.keys(sectionSlotsDict)
        .filter(sectionSlotId => {
          const sectionSlot = sectionSlotsDict[Number(sectionSlotId)];
          const slotStart = new Date(sectionSlot.start);
          const slotDayOfWeek = slotStart.getUTCDay();
          const isSlotInSelectedDaysOfWeek =
            sectionSlotsSelectedDaysOfWeek.has(slotDayOfWeek);
          const isSlotInSelectedTimeFrames = sectionSlotsSelectedTimeFrames.has(
            `${dateToString(sectionSlot.start, 'hh:mm', locale)} - ${dateToString(sectionSlot.end, 'hh:mm', locale)}`,
          );
          const isSlotInSelectedSection = selectedSectionsBulkAssignment.has(
            sectionSlot.id_section,
          );
          const isSlotInSelectedLabel =
            (selectedLabels.has(-1) && sectionSlot.slot_labels.length === 0) ||
            sectionSlot.slot_labels.some(labelId =>
              selectedLabels.has(labelId),
            );

          return (
            isSlotInSelectedDaysOfWeek &&
            isSlotInSelectedTimeFrames &&
            isSlotInSelectedSection &&
            isSlotInSelectedLabel
          );
        })
        .map(Number),
    );
    const assignments: { id_section_slot: number; id_user: number }[] = [];
    const overlaps = findAllOverlappingSlotsRestPeriod(
      Object.values(sectionSlotsDict),
    );
    for (const user of selectedUsersToAssign) {
      const sectionSlotsInWhichTheUserCannotBeAssigned = new Set<number>();
      const userAssignments = epa.assignmentsMap.byUser[user] || [];
      for (const assignment of userAssignments) {
        const overlapsOfAssignment = overlaps[assignment.id_section_slot];
        Array.from(overlapsOfAssignment).forEach(overlap => {
          sectionSlotsInWhichTheUserCannotBeAssigned.add(overlap);
        });
      }
      for (const sectionSlotId of sectionSlotsToAssign) {
        if (!sectionSlotsInWhichTheUserCannotBeAssigned.has(sectionSlotId)) {
          assignments.push({
            id_section_slot: sectionSlotId,
            id_user: user,
          });
        }
      }
    }
    assignSlotMutation.mutate({
      id_itr: idItr,
      assignments,
    });
  };

  return (
    <Wrapper mt="mt-8">
      <Modal
        isOpen={bulkAssignmentModalOpen}
        onClose={() => setBulkAssignmentModalOpen(false)}
        size="2xl"
      >
        <h2 className="font-bold text-lg mb-1">
          {t('manager.shiftAssignment.bulkAssignment')}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {t('manager.shiftAssignment.bulkAssignmentDescription')}
        </p>
        <div className="flex flex-col">
          <label className="block font-semibold mb-3">
            {t('manager.shiftAssignment.selectUsersToBulkAssign')}
          </label>
          <div className="ml-5">
            <UsersFilterDropdown
              users={users}
              roles={roles}
              selectedUsers={selectedUsersToAssign}
              onSelectionChange={setSelectedUsersToAssign}
            />
          </div>
          <label className="block font-semibold my-3">
            {t('manager.shiftAssignment.filterSlotsToAssign')}
          </label>
          <div className="ml-5 flex flex-col gap-2">
            {Object.keys(slotLabels).length > 0 && (
              <>
                <div className="flex flex-row gap-3 items-center">
                  <label className="block text-gray-700 mb-1">
                    {t('manager.sectionsConfig.selectLabels')}
                  </label>
                  <button
                    type="button"
                    className="block text-blue-700 mb-1 text-xs hover:underline"
                    onClick={() =>
                      setSelectedLabels(
                        new Set(
                          Object.values(slotLabels).map(
                            label => label.id_slot_label,
                          ),
                        ),
                      )
                    }
                  >
                    {t('generic.selectAll')}
                  </button>
                  <button
                    type="button"
                    className="block text-red-700 mb-1 text-xs hover:underline"
                    onClick={() => setSelectedLabels(new Set())}
                  >
                    {t('generic.clear')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.values(slotLabels).map(label => (
                    <span
                      key={label.id_slot_label}
                      className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLabels.has(label.id_slot_label)}
                        onChange={() => {
                          setSelectedLabels(prev => {
                            const newSet = new Set(prev);
                            if (prev.has(label.id_slot_label)) {
                              newSet.delete(label.id_slot_label);
                            } else {
                              newSet.add(label.id_slot_label);
                            }
                            return newSet;
                          });
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <LabelBadge
                        labelColor={label.color}
                        icon={label.icon}
                        size="xs"
                      />
                      {label.name}
                    </span>
                  ))}
                  <span className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                    <input
                      type="checkbox"
                      checked={selectedLabels.has(-1)}
                      onChange={() => {
                        setSelectedLabels(prev => {
                          const newSet = new Set(prev);
                          if (prev.has(-1)) {
                            newSet.delete(-1);
                          } else {
                            newSet.add(-1);
                          }
                          return newSet;
                        });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {t('manager.sectionsConfig.noLabel')}
                  </span>
                </div>
              </>
            )}
            {sectionsWithSlots && Object.keys(sectionsWithSlots).length > 1 && (
              <>
                <div className="flex flex-row gap-3 items-center">
                  <label className="block text-gray-700 mb-1">
                    {t('manager.sectionsConfig.selectSections')}
                  </label>
                  <button
                    type="button"
                    className="block text-blue-700 mb-1 text-xs hover:underline"
                    onClick={() =>
                      setSelectedSectionsBulkAssignment(
                        new Set(Object.keys(sections).map(Number)),
                      )
                    }
                  >
                    {t('generic.selectAll')}
                  </button>
                  <button
                    type="button"
                    className="block text-red-700 mb-1 text-xs hover:underline"
                    onClick={() => setSelectedSectionsBulkAssignment(new Set())}
                  >
                    {t('generic.clear')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(sections).map(([sectionId, section]) => (
                    <span
                      key={sectionId}
                      className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSectionsBulkAssignment.has(
                          Number(sectionId),
                        )}
                        onChange={() => {
                          setSelectedSectionsBulkAssignment(prev => {
                            const newSet = new Set(prev);
                            if (prev.has(Number(sectionId))) {
                              newSet.delete(Number(sectionId));
                            } else {
                              newSet.add(Number(sectionId));
                            }
                            return newSet;
                          });
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {section.name}
                    </span>
                  ))}
                </div>
              </>
            )}
            <div className="flex flex-row gap-3 items-center">
              <label className="block text-gray-700 mb-1">
                {t('manager.sectionsConfig.selectDaysOfWeek')}
              </label>
              <button
                type="button"
                className="block text-blue-700 mb-1 text-xs hover:underline"
                onClick={() =>
                  setSectionSlotsSelectedDaysOfWeek(
                    new Set(Object.keys(daysOfWeek).map(Number)),
                  )
                }
              >
                {t('generic.selectAll')}
              </button>
              <button
                type="button"
                className="block text-red-700 mb-1 text-xs hover:underline"
                onClick={() => setSectionSlotsSelectedDaysOfWeek(new Set())}
              >
                {t('generic.clear')}
              </button>
            </div>
            <div className="flex space-x-2">
              {daysOfWeek.map((day, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={sectionSlotsSelectedDaysOfWeek.has(
                      getFirstDayOfWeek(locale) === 1 ? (index + 1) % 7 : index,
                    )}
                    onChange={() =>
                      setSectionSlotsSelectedDaysOfWeek(prev => {
                        const newSet = new Set(prev);
                        const normalizedIndex =
                          getFirstDayOfWeek(locale) === 1
                            ? (index + 1) % 7
                            : index;
                        if (prev.has(normalizedIndex)) {
                          newSet.delete(normalizedIndex);
                        } else {
                          newSet.add(normalizedIndex);
                        }
                        return newSet;
                      })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {day}
                </span>
              ))}
            </div>
            <div className="flex flex-row gap-3 items-center">
              <label className="block text-gray-700 mb-1 mt-2">
                {t('manager.sectionsConfig.selectTimeFrames')}
              </label>
              <button
                type="button"
                className="block text-blue-700 mb-1 mt-2 text-xs hover:underline"
                onClick={() =>
                  setSectionSlotsSelectedTimeFrames(
                    new Set(sectionSlotsUniqueTimeFrames),
                  )
                }
              >
                {t('generic.selectAll')}
              </button>
              <button
                type="button"
                className="block text-red-700 mb-1 mt-2 text-xs hover:underline"
                onClick={() => setSectionSlotsSelectedTimeFrames(new Set())}
              >
                {t('generic.clear')}
              </button>
            </div>
            <div className="flex space-x-2">
              {sectionSlotsUniqueTimeFrames.map(timeFrame => (
                <span
                  key={timeFrame}
                  className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={sectionSlotsSelectedTimeFrames.has(timeFrame)}
                    onChange={() => {
                      setSectionSlotsSelectedTimeFrames(prev => {
                        const newSet = new Set(prev);
                        if (prev.has(timeFrame)) {
                          newSet.delete(timeFrame);
                        } else {
                          newSet.add(timeFrame);
                        }
                        return newSet;
                      });
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {timeFrame}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-row items-center gap-3">
            <YSButton
              onClick={bulkAssignSlots}
              classNames="mt-3"
              loading={assignSlotMutation.isPending}
              disabled={selectedUsersToAssign.size === 0}
            >
              {t('manager.shiftAssignment.bulkAssignPeople')}
            </YSButton>
          </div>
        </div>
      </Modal>
      <div className="flex flex-row gap-1 flex-wrap justify-between">
        <ShiftLabelLegend labels={Object.values(slotLabels)} />
        <div className="flex flex-row gap-2 lg:gap-4 items-center">
          <div className="flex items-center pr-2 lg:pr-4 border-r border-r-gray-400">
            <input
              id="show-libranza"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={showRestPeriod}
              onChange={() => setShowRestPeriod(!showRestPeriod)}
            />
            <label
              htmlFor="show-libranza"
              className="ml-3 text-sm font-medium text-gray-900"
            >
              {t('manager.iterationVerification.showRestPeriod')}
            </label>
          </div>

          <div className="pr-2 lg:pr-4 border-r border-r-gray-400">
            <UsersFilterDropdown
              users={users}
              roles={roles}
              selectedUsers={selectedPeople}
              onSelectionChange={setSelectedPeople}
            />
          </div>
          <FilterDropdown
            label={t('generic.sections')}
            items={sectionsWithSlots.map(({ section }) => ({
              id: String(section.id_section),
              name: section.name,
            }))}
            selectedItems={selectedSectionsFilter}
            onSelectionChange={setSelectedSectionsFilter}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVirgueriaLayout(VirgueriaLayoutMode.HORIZONTAL)}
              className={`p-2 rounded-md ${
                virgueriaLayout === VirgueriaLayoutMode.HORIZONTAL
                  ? 'bg-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <ArrowsRightLeftIcon
                className={`w-4 h-4 ${
                  virgueriaLayout === VirgueriaLayoutMode.HORIZONTAL
                    ? 'text-gray-800'
                    : 'text-gray-400'
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => setVirgueriaLayout(VirgueriaLayoutMode.VERTICAL)}
              className={`p-2 rounded-md ${
                virgueriaLayout === VirgueriaLayoutMode.VERTICAL
                  ? 'bg-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <ArrowsUpDownIcon
                className={`w-4 h-4 ${
                  virgueriaLayout === VirgueriaLayoutMode.VERTICAL
                    ? 'text-gray-800'
                    : 'text-gray-400'
                }`}
              />
            </button>
            <button
              className="flex flex-row gap-2 items-center bg-gray-100 rounded-md p-1.5 hover:bg-gray-200"
              onClick={() => setBulkAssignmentModalOpen(true)}
            >
              <UsersIcon className="text-gray-500 w-3 h-3" />
              {t('manager.shiftAssignment.bulkAssignment')}
            </button>
          </div>
        </div>
      </div>
      {virgueriaLayout === VirgueriaLayoutMode.HORIZONTAL ? (
        // <ScheduleGridAssignment
        //   start={iteration.start_day}
        //   end={iteration.end_day}
        //   sectionsWithSlots={sectionsWithSlots.filter(({ section }) =>
        //     selectedSections.has(String(section.id_section)),
        //   )}
        //   selectedPeople={selectedPeople}
        //   showRestPeriod={showRestPeriod}
        // />
        <Virgueria
          start={iteration.start_day}
          end={iteration.end_day}
          sectionsWithSlots={sectionsWithSlots.filter(({ section }) =>
            selectedSectionsFilter.has(String(section.id_section)),
          )}
          version={VirgueriaVersion.Virgueria}
          epa={epa}
          users={users}
          labels={Object.values(slotLabels)}
          showRestPeriod={showRestPeriod}
          selectedPeople={selectedPeople}
        />
      ) : (
        <VerticalVirgueria
          start={iteration.start_day}
          end={iteration.end_day}
          sectionsWithSlots={sectionsWithSlots.filter(({ section }) =>
            selectedSectionsFilter.has(String(section.id_section)),
          )}
          showRestPeriod={showRestPeriod}
          selectedPeople={selectedPeople}
        />
      )}
    </Wrapper>
  );
}
